import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationsService } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { notificationsKeys } from 'app/queries/notifications/notificationsKeys';
import toast from 'react-hot-toast';

const toastActionError = (error: Error, fallback: string) => {
  if (error instanceof ApiCallError) {
    notifyApiCallError(error);
    return;
  }
  toast.error(fallback);
};

export const useNotificationsPopoverActions = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { updateLastNotifications } = useAuthContext();

  const refreshNotifications = () =>
    Promise.all([updateLastNotifications(), queryClient.invalidateQueries({ queryKey: notificationsKeys.list() })]);

  const markAllReadMutation = useMutation({
    mutationFn: () => call(() => NotificationsService.setAllNotificationsRead()),
    onError: (error) => toastActionError(error, 'Failed to mark notifications as read'),
    onSettled: refreshNotifications,
  });

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => call(() => NotificationsService.setNotificationRead({ notificationId })),
    onError: (error) => toastActionError(error, 'Failed to mark the notification as read'),
    onSettled: refreshNotifications,
  });

  const dismissMutation = useMutation({
    mutationFn: (notificationId: string) => call(() => NotificationsService.deleteNotification({ notificationId })),
    onError: (error) => toastActionError(error, 'Failed to delete the notification'),
    onSettled: refreshNotifications,
  });

  return {
    isMarkingAllRead: markAllReadMutation.isPending,
    markAllRead: () => markAllReadMutation.mutate(),
    markRead: (notificationId: string) => markReadMutation.mutate(notificationId),
    dismiss: (notificationId: string) => dismissMutation.mutate(notificationId),
  };
};
